/**
 * Pending-refresh handoff between the service worker and the app.
 *
 * An admin "Refresh" tap arrives as a push. On Android the service worker can
 * just message the open window and it reloads on the spot. On iOS it usually
 * can't: a Home Screen web app is suspended the moment it leaves the
 * foreground, so there's no window client left to talk to. The SW records the
 * request here instead, and the app honours it the next time it runs.
 *
 * Shares the 'CHSPSettings' DB the notification settings already use — same
 * version (2) and same 'settings' store, so opening it never triggers an
 * upgrade that would block the other readers.
 */

const DB_NAME = 'CHSPSettings';
const DB_VERSION = 2;
const STORE = 'settings';
const KEY = 'pendingRefresh';

export const REFRESH_NOTIFICATION_TAG = 'taru-force-refresh';

function openDB() {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
  });
}

/**
 * Read the pending-refresh marker and clear it in the same transaction.
 *
 * Clearing before the caller reloads is what stops a reload loop: the marker
 * is gone by the time the fresh page boots and checks again.
 *
 * @returns {Promise<boolean>} whether a refresh was owed.
 */
export async function takePendingRefresh() {
  let db;
  try {
    db = await openDB();
  } catch {
    return false;
  }

  return new Promise((resolve) => {
    let found = false;
    try {
      const tx = db.transaction([STORE], 'readwrite');
      const store = tx.objectStore(STORE);
      const request = store.get(KEY);
      request.onsuccess = () => {
        if (request.result) {
          found = true;
          store.delete(KEY);
        }
      };
      // Resolve on transaction completion, not on the get, so the delete is
      // durable before anyone acts on the answer.
      tx.oncomplete = () => resolve(found);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

/** Dismiss any refresh notification left behind once we've acted on it. */
export async function closeRefreshNotifications() {
  try {
    if (!('serviceWorker' in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;
    const notifications = await registration.getNotifications({
      tag: REFRESH_NOTIFICATION_TAG,
    });
    notifications.forEach((n) => n.close());
  } catch {
    // Dismissing is cosmetic — never let it block the reload.
  }
}
